import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../services/service";
const BASE_URL = process.env.REACT_APP_BASE_API_URL;
type BaseResponse<T> = {
  message: string;
  successful: boolean;
  status: number;
  data: T;
};

export type AssessmentAndSubmissionResponse = {
  assessmentGuideline: string;
  assessmentId: number;
  attachmentList: Array<string>;
  candidateAttachmentList: Array<string>;
  candidateId: string;
  createdAt: string;
  createdById: string;
  createdByName: string;
  feedBackText: string | null;
  isPassed: boolean;
  jobId: string;
  stage: string;
  submissionAt: string | null;
  submissionText: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  urlList: Array<string>;
};

type AssessmentSubmissionRes = {
  status: number;
  data: any;
};
type AssessmentSubmissionReq = {
  assessmentId: string;
  stage: string;
  candidateId: string;
  jobId: string;
  submissionText?: string;
  attachmentList?: string[];
};
type GenerateUploadLinkRequest = {
  fileName: string;
  id: string;
};
type GenerateUploadLinkResponse = {
  link: string;
};

export type AddAssesmentGuidilineRequest = {
  assessmentGuideline: string;
  attachmentList: Array<string>;
  urlList: Array<string>;
  candidateId: string;
  jobId: string;
  createdById: string;
  createdByName: string;
  stage: string;
  isPassed: boolean;
};

type AddAssesmentGuidilineResponse = BaseResponse<any>;

type GetAssessmentRequest = {
  candidateId: string;
  jobId: string;
  stage: string;
};
type GetAssessmentResponse = BaseResponse<
  Array<AssessmentAndSubmissionResponse>
>;

type GetSubmissionRequest = {
  candidateId: string;
  jobId: string;
  stage: string;
};
type GetSubmissionResponse = BaseResponse<AssessmentAndSubmissionResponse>;

export type AddFeedbackRequest = {
  assessmentId: number;
  isPassed: boolean;
  candidateId: string;
  jobId: string;
  feedBackText: string;
};

type AddFeedbackResponse = BaseResponse<any>;

export const assessmentApi = createApi({
  reducerPath: "assessmentApi",
  baseQuery: axiosBaseQuery({
    baseUrl: BASE_URL || "",
  }),
  tagTypes: ["assessment"],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    submitAssessment: builder.mutation<
      AssessmentSubmissionRes,
      AssessmentSubmissionReq
    >({
      query: (request) => ({
        url: `submitAssessmentChallenge`,
        method: "POST",
        body: request,
        requiredGuestAuth: true,
      }),
      invalidatesTags: ["assessment"],
    }),
    generateUploadLink: builder.mutation<
      GenerateUploadLinkResponse,
      GenerateUploadLinkRequest
    >({
      query: (request) => ({
        url: "generateUploadLinkAssessment",
        method: "POST",
        requiredGuestAuth: true,
        body: {
          fileName: request.fileName,
        },
      }),
    }),
    addAssessmentGuideline: builder.mutation<
      AddAssesmentGuidilineResponse,
      AddAssesmentGuidilineRequest
    >({
      query: (request) => ({
        url: `addAssessmentGuideline`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["assessment"],
    }),
    getAssessment: builder.query<GetAssessmentResponse, GetAssessmentRequest>({
      query: (request) => ({
        url: `getAssessment`,
        method: "POST",
        body: request,
      }),
      providesTags: ["assessment"],
    }),
    getSubmission: builder.query<GetSubmissionResponse, GetSubmissionRequest>({
      query: (request) => ({
        url: `getAssessmentSubmission`,
        method: "POST",
        body: request,
      }),
      providesTags: ["assessment"],
    }),
    addFeedback: builder.mutation<AddFeedbackResponse, AddFeedbackRequest>({
      query: (request) => ({
        url: `addAssessmentFeedback`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["assessment"],
    }),
  }),
});

export const {
  useSubmitAssessmentMutation,
  useGenerateUploadLinkMutation,
  useAddAssessmentGuidelineMutation,
  useAddFeedbackMutation,
  useGetAssessmentQuery,
  useGetSubmissionQuery,
} = assessmentApi;
